import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Tabs, DatePicker, Card, Space, Button, Switch, Select, Typography, Tooltip, message } from "antd";
import {
  BarChartOutlined,
  StockOutlined,
  DatabaseOutlined,
  MailOutlined,
  ReloadOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { setSelectedClients } from "../../../../core/redux/action";

const { Text } = Typography;
import dayjs from "dayjs";
import { SALES_DASHBOARD_FILTER_TZ, getDashboardEmailRecipients, saveDashboardEmailRecipients, shareDashboardEmail } from "../../../../core/api/data";
import { getAllUsers } from "../../../../core/api/settings";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";
import DailySalesReport from "./DailySalesReport";
import MergedStockUpdate from "./MergedStockUpdate";
import MergedSalesDump from "./MergedSalesDump";
import MergedMailingLTV from "./MergedMailingLTV";

const { RangePicker } = DatePicker;

const SESSION_KEY = "salesDashboard_state_v3";

const loadSessionState = () => {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    if (parsed.dateFrom && parsed.dateTo) {
      parsed.dateRange = [
        dayjs(parsed.dateFrom).startOf("day"),
        dayjs(parsed.dateTo).endOf("day"),
      ];
    }
    return parsed;
  } catch {
    return null;
  }
};

const saveSessionState = (state) => {
  try {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        activeTab: state.activeTab,
        dateFrom: state.dateRange[0]?.toISOString(),
        dateTo: state.dateRange[1]?.toISOString(),
      })
    );
  } catch { /* silent */ }
};

const SalesDashboard = () => {
  const { selectedBrands, brandIds } = useGlobalFilters();
  const dispatch = useDispatch();
  const clients = useSelector((state) => state.clients, shallowEqual) ?? [];
  const selectedClients = useSelector((state) => state.selectedClients, shallowEqual) ?? [];
  const selectAllRef = useRef(false);

  useEffect(() => {
    if (selectAllRef.current || clients.length === 0) return;
    selectAllRef.current = true;

    const allClientIds = clients.map((c) => String(c.id));
    const alreadyAll = selectedClients.length === allClientIds.length &&
      allClientIds.every((id) => selectedClients.includes(id));

    if (!alreadyAll) {
      dispatch(setSelectedClients(allClientIds));
      localStorage.setItem("selectedClients", JSON.stringify(allClientIds));
    }
  }, [clients, selectedClients, dispatch]);

  const savedState = useRef(loadSessionState());

  const [activeTab, setActiveTab] = useState(
    savedState.current?.activeTab || "daily-sales"
  );
  const [dateRange, setDateRange] = useState(
    savedState.current?.dateRange || [dayjs().startOf("day"), dayjs().endOf("day")]
  );
  const [refreshKey, setRefreshKey] = useState(0);

  // Persist active tab + date range to sessionStorage on change
  useEffect(() => {
    saveSessionState({ activeTab, dateRange });
  }, [activeTab, dateRange]);

  const brandNameMap = useMemo(() => {
    const map = {};
    selectedBrands.forEach((brand) => {
      map[brand.id] = brand.brand_name;
    });
    return map;
  }, [selectedBrands]);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const intervalRef = useRef(null);

  // Cron email recipients
  const [cronRecipients, setCronRecipients] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  useEffect(() => {
    getAllUsers()
      .then((res) => {
        const users = (res?.data || res || []).filter((u) => u.email);
        setUserOptions(
          users.map((u) => ({
            label: u.email + (u.first_name ? ` (${u.first_name}${u.last_name ? " " + u.last_name : ""})` : ""),
            value: u.email,
          }))
        );
      })
      .catch((e) => { console.warn("Failed to load users", e); });
    getDashboardEmailRecipients()
      .then((res) => setCronRecipients(res?.recipients || []))
      .catch((e) => { console.warn("Failed to load cron recipients", e); });
  }, []);

  const handleCronRecipientsChange = useCallback(async (values) => {
    setCronRecipients(values);
    try {
      await saveDashboardEmailRecipients(values);
      message.success(`Daily email recipients updated (${values.length})`);
    } catch {
      message.error("Failed to save recipients");
    }
  }, []);

  // One-time share
  const [shareEmails, setShareEmails] = useState([]);
  const [shareTabs, setShareTabs] = useState([]);
  const [shareSending, setShareSending] = useState(false);

  const SHARE_TAB_OPTIONS = [
    { label: "Daily Sales Report", value: "daily-sales" },
    { label: "Merged Stock Update", value: "stock-update" },
    { label: "Merged Sales Dump", value: "sales-dump" },
    { label: "Merged Mailing/LTV", value: "mailing-ltv" },
  ];

  const handleShareSend = useCallback(async () => {
    if (!shareEmails.length) return message.warning("Select at least one recipient");
    if (!shareTabs.length) return message.warning("Select at least one tab to share");
    setShareSending(true);
    try {
      const dr = dateRange?.length === 2
        ? [dateRange[0].utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"), dateRange[1].utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")]
        : [];
      await shareDashboardEmail(shareEmails, shareTabs, dr);
      message.success(`Shared to ${shareEmails.length} recipient(s)`);
      setShareEmails([]);
      setShareTabs([]);
    } catch (err) {
      message.error(err?.response?.data?.error || "Failed to send");
    } finally {
      setShareSending(false);
    }
  }, [shareEmails, shareTabs, dateRange]);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      intervalRef.current = setInterval(() => {
        setRefreshKey((prev) => prev + 1);
      }, refreshInterval * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, refreshInterval]);

  return (
    <div
      style={{ padding: "0", margin: "0", width: "100%", minHeight: "100vh" }}
    >
      <div style={{ padding: "16px", margin: "0" }}>
        <div style={{ marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a2e" }}>
            Sales Dashboard
          </h2>
          <Space size={12}>
            <Space size={6} style={{ alignItems: "center" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Daily email to:</Text>
              <Select
                mode="multiple"
                size="small"
                allowClear
                placeholder="Select users"
                style={{ minWidth: 170, maxWidth: 300 }}
                value={cronRecipients}
                onChange={handleCronRecipientsChange}
                options={userOptions}
                optionFilterProp="label"
                maxTagCount={0}
                maxTagPlaceholder={(omitted) => `${omitted.length} user${omitted.length !== 1 ? "s" : ""}`}
              />
            </Space>
            <Space size={6} style={{ alignItems: "center" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Auto-Refresh:</Text>
              <Switch
                size="small"
                checked={autoRefresh}
                onChange={setAutoRefresh}
              />
              {autoRefresh && (
                <Select
                  size="small"
                  value={refreshInterval}
                  onChange={setRefreshInterval}
                  style={{ width: 90 }}
                  options={[
                    { label: "30s", value: 30 },
                    { label: "1 min", value: 60 },
                    { label: "2 min", value: 120 },
                    { label: "5 min", value: 300 },
                  ]}
                />
              )}
            </Space>
          </Space>
        </div>

        {/* Share / Mail row */}
        <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>Share via email:</Text>
          <Select
            mode="tags"
            size="small"
            allowClear
            placeholder="Type or pick emails..."
            style={{ minWidth: 200, maxWidth: 340 }}
            value={shareEmails}
            onChange={setShareEmails}
            options={userOptions}
            optionFilterProp="label"
            maxTagCount={0}
            maxTagPlaceholder={(omitted) => `${omitted.length} recipient${omitted.length !== 1 ? "s" : ""}`}
            tokenSeparators={[",", " "]}
          />
          <Select
            mode="multiple"
            size="small"
            allowClear
            placeholder="Select tabs..."
            style={{ minWidth: 180, maxWidth: 340 }}
            value={shareTabs}
            onChange={setShareTabs}
            options={SHARE_TAB_OPTIONS}
            maxTagCount={0}
            maxTagPlaceholder={(omitted) => `${omitted.length} tab${omitted.length !== 1 ? "s" : ""}`}
          />
          <Button
            size="small"
            type="primary"
            icon={<SendOutlined />}
            onClick={handleShareSend}
            loading={shareSending}
            disabled={!shareEmails.length || !shareTabs.length}
            style={{ borderRadius: 6 }}
          >
            Send
          </Button>
        </div>

        {/* Date Controls */}
        <Card
          size="small"
          bordered={false}
          style={{ marginBottom: 16, borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
          bodyStyle={{ padding: "12px 20px" }}
        >
          <Space wrap size={8}>
            {[
              { label: "Today", range: [dayjs().startOf("day"), dayjs().endOf("day")] },
              { label: "Yesterday", range: [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")] },
              {
                label: "Last 3 days",
                range: [dayjs().subtract(2, "day").startOf("day"), dayjs().endOf("day")],
              },
              {
                label: "Last 7 days",
                range: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")],
              },
              { label: "This month", range: [dayjs().startOf("month"), dayjs().endOf("day")] },
              {
                label: "Last month",
                range: [
                  dayjs().subtract(1, "month").startOf("month"),
                  dayjs().subtract(1, "month").endOf("month"),
                ],
              },
            ].map((preset) => {
              const isActive =
                dateRange[0]?.isSame(preset.range[0], "day") &&
                dateRange[1]?.isSame(preset.range[1], "day");
              return (
                <Button
                  key={preset.label}
                  size="small"
                  type={isActive ? "primary" : "default"}
                  style={{ borderRadius: 6, fontWeight: isActive ? 600 : 400 }}
                  onClick={() => setDateRange(preset.range)}
                >
                  {preset.label}
                </Button>
              );
            })}
            <Tooltip
              title={`Each selected date is a full calendar day in US Eastern (${SALES_DASHBOARD_FILTER_TZ}): midnight → 11:59:59.999 PM, sent to the API as UTC. Same interpretation for presets.`}
            >
              <span>
                <RangePicker
                  size="small"
                  value={dateRange}
                  onChange={(dates) => {
                    if (dates && dates.length === 2) {
                      setDateRange([dates[0].startOf("day"), dates[1].endOf("day")]);
                    }
                  }}
                  format="YYYY-MM-DD"
                  style={{ borderRadius: 6 }}
                />
              </span>
            </Tooltip>
            <Button
              size="small"
              type="primary"
              ghost
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              style={{ borderRadius: 6 }}
            >
              Refresh All
            </Button>
          </Space>
        </Card>

        {/* Tabs */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          destroyInactiveTabPane={false}
          items={[
            {
              key: "daily-sales",
              label: <span><BarChartOutlined /> Daily Sales Report</span>,
              children: (
                <DailySalesReport
                  brandIds={brandIds}
                  brandNameMap={brandNameMap}
                  dateRange={dateRange}
                  refreshKey={refreshKey}
                  selectedBrands={selectedBrands}
                  isActive={activeTab === "daily-sales"}
                />
              ),
            },
            {
              key: "stock-update",
              label: <span><StockOutlined /> Merged Stock Update</span>,
              children: (
                <MergedStockUpdate
                  brandIds={brandIds}
                  brandNameMap={brandNameMap}
                  dateRange={dateRange}
                  refreshKey={refreshKey}
                  selectedBrands={selectedBrands}
                  isActive={activeTab === "stock-update"}
                />
              ),
            },
            {
              key: "sales-dump",
              label: <span><DatabaseOutlined /> Merged Sales Dump</span>,
              children: (
                <MergedSalesDump
                  brandIds={brandIds}
                  brandNameMap={brandNameMap}
                  dateRange={dateRange}
                  refreshKey={refreshKey}
                  selectedBrands={selectedBrands}
                  isActive={activeTab === "sales-dump"}
                />
              ),
            },
            {
              key: "mailing-ltv",
              label: <span><MailOutlined /> Merged Mailing/LTV</span>,
              children: (
                <MergedMailingLTV
                  brandIds={brandIds}
                  brandNameMap={brandNameMap}
                  dateRange={dateRange}
                  refreshKey={refreshKey}
                  selectedBrands={selectedBrands}
                  isActive={activeTab === "mailing-ltv"}
                />
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default SalesDashboard;
